/**
 * Auth API — v2 BE only.
 *
 * All admin auth targets the new BE (`/admin/auth/*`). The legacy `/api2 /api3`
 * login/logout/password paths were removed as part of the v2-only migration.
 * New BE login sets `adminconnect.sid` on its own host; success/failure is
 * confirmed by the caller via fetchEnvironment.
 */
import webApiAdmin from '@/lib/api-common/webApiAdmin';
import { getNewApiBaseUrl } from '@/lib/api-common/config';

export interface AuthCallResult {
  ok: boolean;
  message?: string;
}

/**
 * Logout — GET new BE /admin/auth/logout.
 */
export async function logout(): Promise<void> {
  await fetch(`${getNewApiBaseUrl()}/admin/auth/logout`, {
    method: 'GET',
    credentials: 'include',
  }).catch((err) => {
    if (import.meta.env.DEV) console.warn('[auth] new BE logout failed:', err);
  });
}

/**
 * Sign in with loginId + password — POST new BE /admin/auth/login (JSON body).
 * Sets `adminconnect.sid` on the new BE host. Success confirmed by the caller
 * via fetchEnvironment.
 */
export async function login(loginId: string, password: string): Promise<AuthCallResult> {
  try {
    // postSilent: skip the global error toast; auth handles its own messaging.
    await webApiAdmin.postSilent('admin/auth/login', { loginId, password });
    return { ok: true };
  } catch (err) {
    const e = err as { message?: string; code?: number };
    return { ok: false, message: e?.message ?? `HTTP ${e?.code ?? 'unknown'}` };
  }
}

/**
 * Guest/advertiser magic-link login via campaign guestToken.
 *
 * The advertiser link is `${admin}/login/:token`, where `token` is the campaign
 * `guestToken` — a NEW-BE artifact validated only at the new BE's
 * `GET /advertiser/auth/login/:token`. Uses the `/api-v2` prefix
 * (getNewApiBaseUrl) so the advertiser session cookie is set same-origin via
 * CloudFront / the Vite proxy. Success/failure is confirmed by the caller via
 * fetchEnvironment — this only establishes the cookie; failures are swallowed.
 */
export async function guestLogin(token: string): Promise<void> {
  const encoded = encodeURIComponent(token);
  await fetch(`${getNewApiBaseUrl()}/advertiser/auth/login/${encoded}`, {
    method: 'GET',
    credentials: 'include',
  }).catch((err) => {
    if (import.meta.env.DEV) console.warn('[auth] new BE guest login failed:', err);
  });
}

/**
 * Request a password reset email — POST /admin/auth/request-password-reset.
 */
export async function requestPasswordReset(email: string): Promise<AuthCallResult> {
  try {
    await webApiAdmin.postSilent('admin/auth/request-password-reset', { email });
    return { ok: true };
  } catch (err) {
    const e = err as { message?: string; code?: number };
    return { ok: false, message: e?.message ?? `HTTP ${e?.code ?? 'unknown'}` };
  }
}

/**
 * Apply a password reset using a token from the reset email —
 * POST /admin/auth/reset-password/:token.
 */
export async function resetPassword(
  token: string,
  newPassword: string,
  confirmPassword: string,
): Promise<AuthCallResult> {
  try {
    await webApiAdmin.postSilent(`admin/auth/reset-password/${encodeURIComponent(token)}`, {
      newPassword,
      confirmPassword,
    });
    return { ok: true };
  } catch (err) {
    const e = err as { message?: string; code?: number };
    return { ok: false, message: e?.message ?? `HTTP ${e?.code ?? 'unknown'}` };
  }
}

/**
 * Change the current admin's password — POST /admin/auth/change-password.
 * BE uses snake_case-ish field names on the new endpoint.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  confirmPassword: string,
): Promise<AuthCallResult> {
  try {
    await webApiAdmin.postSilent('admin/auth/change-password', {
      oldPassword: currentPassword,
      newPassword,
      newPasswordRepeat: confirmPassword,
    });
    return { ok: true };
  } catch (err) {
    const e = err as { message?: string; code?: number };
    return { ok: false, message: e?.message ?? `HTTP ${e?.code ?? 'unknown'}` };
  }
}
